import * as React from "react"
import * as Yup from "yup"
import { SelfHostedGitConnectionAppUrls } from "../constants"
import CopyLink from "@modules/build/details/components/CopyLink.js"
import {
  selfHostedGitConnection as text,
  enterpriseConnectionForm as formText,
} from "@modules/locales/default"

import {
  ModalPanel,
  StyledPanel,
  StyledPanelHeader,
  StyledPanelBodySection,
  StyledPanelActions,
  InputConnectedField,
  TextAreaConnectedField,
  Button,
  Text,
  Link,
  Heading,
  Notification,
} from "gatsby-interface"

import { MdLaunch, MdError } from "react-icons/md"

import { Form } from "@modules/form/components/Form"
import { SourceControlProvider } from "@modules/graphql/types"

export type GithubFormValues = {
  instanceUrl: string
  appId: string
  privateKey: string
  clientId: string
  clientSecret: string
  webhookSecret: string
  name: string
}

const validationRegEx = {
  privateKeyStart: /^-----BEGIN RSA PRIVATE KEY-----/,
  privateKeyEnd: /-----END RSA PRIVATE KEY-----$/,
}

const validationSchema = Yup.object().shape<GithubFormValues>({
  appId: Yup.string().required(formText.messages.validationIsRequired),
  instanceUrl: Yup.string()
    .url(formText.messages.validationUrl)
    .required(formText.messages.validationInvalidUrl),
  privateKey: Yup.string()
    .matches(validationRegEx.privateKeyStart, formText.messages.privateKeyStart)
    .matches(validationRegEx.privateKeyEnd, formText.messages.privateKeyEnd)
    .required(formText.messages.privateKeyRequired),
  clientId: Yup.string().required(formText.messages.validationIsRequired),
  clientSecret: Yup.string().required(formText.messages.validationIsRequired),
  webhookSecret: Yup.string().required(formText.messages.validationIsRequired),
  name: Yup.string().required(formText.messages.validationIsRequired),
})

function AppValueField({ label, value }: { label: string; value: string }) {
  return (
    <div css={theme => ({ marginBottom: theme.space[5] })}>
      <Text as="div">{label}</Text>
      <div
        css={theme => ({
          alignItems: `center`,
          backgroundColor: theme.colors.white,
          border: `1px solid ${theme.colors.grey[20]}`,
          borderRadius: theme.radii[2],
          display: `flex`,
          justifyContent: `space-between`,
          padding: `${theme.space[2]} ${theme.space[4]}`,
        })}
      >
        <Text
          as="span"
          size="S"
          css={theme => ({
            fontFamily: theme.fonts.monospace,
            fontSize: theme.fontSizes[0],
            overflowWrap: `anywhere`,
          })}
        >
          {value}
        </Text>
        <CopyLink
          label="Copy"
          successLabel="Copied!"
          content={value}
          onClick={null}
        />
      </div>
    </div>
  )
}

function InstructionsSection() {
  return (
    <StyledPanelBodySection>
      <Text>{text.messages.instructions}</Text>
      <Link
        css={theme => ({ fontWeight: theme.fontWeights.semiBold })}
        href="https://support.gatsbyjs.com/hc/en-us/articles/4495503600275"
        target="_blank"
      >
        {text.messages.instructionsLink}
        <MdLaunch css={theme => ({ marginLeft: theme.space[2] })} />
      </Link>
    </StyledPanelBodySection>
  )
}

function GithubAppValues({ organizationId }: { organizationId: string }) {
  const source = SourceControlProvider.GithubEnterprise
  return (
    <StyledPanelBodySection
      css={theme => ({
        backgroundColor: theme.colors.grey[5],
        borderTop: `1px solid ${theme.colors.grey[20]}`,
        borderBottom: `1px solid ${theme.colors.grey[20]}`,
      })}
    >
      <Heading
        css={theme => ({
          fontSize: theme.fontSizes[3],
          marginBottom: theme.space[3],
        })}
      >
        {text.headers.createApp}
      </Heading>
      <Text
        css={theme => ({
          marginBottom: theme.space[5],
        })}
      >
        {text.messages.createApp}
      </Text>
      <AppValueField
        label={text.labels.callbackUrl}
        value={SelfHostedGitConnectionAppUrls[source].CallbackUrl}
      />
      <AppValueField
        label={text.labels.setupUrl}
        value={SelfHostedGitConnectionAppUrls[source].SetupUrl}
      />
      <AppValueField
        label={text.labels.webhookUrl}
        value={`${SelfHostedGitConnectionAppUrls[source].WebhookUrl}/${organizationId}`}
      />
    </StyledPanelBodySection>
  )
}

export function GithubModalPanel({
  onSubmit,
  organizationId,
  onClose,
  error,
}: {
  onSubmit: (input: GithubFormValues) => void
  organizationId: string
  onClose: () => void
  error?: string | null
}) {
  return (
    <ModalPanel maxWidth="720px" css={{ width: `100%` }}>
      <Form<GithubFormValues>
        validationSchema={validationSchema}
        initialValues={{
          instanceUrl: "",
          appId: "",
          privateKey: "",
          clientId: "",
          clientSecret: "",
          webhookSecret: "",
          name: "",
        }}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, isValid, dirty, setFieldValue }) => (
          <StyledPanel>
            <StyledPanelHeader onCloseButtonClick={onClose}>
              {text.headers.modal}
            </StyledPanelHeader>
            <InstructionsSection />
            <GithubAppValues organizationId={organizationId} />
            <StyledPanelBodySection css={{ paddingBottom: 0 }}>
              <Heading
                css={theme => ({
                  fontSize: theme.fontSizes[3],
                  marginBottom: theme.space[3],
                })}
              >
                {text.headers.connectApp}
              </Heading>
              <Text noMarginBottom>{text.messages.connectApp}</Text>
            </StyledPanelBodySection>
            <Form.FormElement applySpacing noValidate autoComplete="off">
              <StyledPanelBodySection
                css={theme => ({
                  display: `flex`,
                  flexDirection: `column`,
                  gap: theme.space[5],
                })}
              >
                <InputConnectedField
                  name="instanceUrl"
                  label={text.labels.instanceUrl}
                  placeholder="https://"
                  required
                />
                <InputConnectedField
                  name="appId"
                  label={text.labels.appId}
                  placeholder="GitHub App ID"
                  required
                />
                <TextAreaConnectedField
                  name="privateKey"
                  label={text.labels.privateKey}
                  placeholder="-----BEGIN RSA PRIVATE KEY-----"
                  onChange={e => {
                    const { value } = e.target
                    setFieldValue(
                      "privateKey",
                      value.trim().replace(/\\n/g, "\n")
                    )
                  }}
                  required
                />
                <InputConnectedField
                  name="clientId"
                  label={text.labels.clientID}
                  placeholder="GitHub App Client ID"
                  required
                />
                <InputConnectedField
                  name="clientSecret"
                  label={text.labels.clientSecret}
                  placeholder="GitHub App Client Secret"
                  required
                />
                <InputConnectedField
                  name="webhookSecret"
                  label={text.labels.webhookSecret}
                  placeholder="GitHub App Webhook Secret"
                  required
                />
                <InputConnectedField
                  name="name"
                  label={text.labels.displayName}
                  placeholder="the value placed here will be what is shown in your workspace"
                  required
                />
              </StyledPanelBodySection>
              <StyledPanelActions>
                <div css={{ width: `100%` }}>
                  {error ? (
                    <Notification
                      css={theme => ({
                        marginBottom: theme.space[7],
                      })}
                      tone="DANGER"
                      variant="SECONDARY"
                      content={error}
                      Icon={MdError}
                    />
                  ) : null}
                  <div
                    css={{
                      display: `flex`,
                      justifyContent: `space-between`,
                    }}
                  >
                    <Button onClick={onClose} variant="SECONDARY">
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      loading={isSubmitting}
                      loadingLabel={text.messages.validatingConnection}
                      disabled={!dirty || !isValid}
                    >
                      {text.actions.saveConnection}
                    </Button>
                  </div>
                </div>
              </StyledPanelActions>
            </Form.FormElement>
          </StyledPanel>
        )}
      </Form>
    </ModalPanel>
  )
}
